import type {
  ClosingCostMigration,
  FinancingType,
  LoanCarryMethod,
  PointsBasis,
  RehabBudgetMode,
  RehabCostEntryMode,
  RehabInterestAssumption,
} from '../engine/types';

export type {
  FinancingType,
  ClosingCostMigration,
  RehabBudgetMode,
  RehabCostEntryMode,
  LoanCarryMethod,
  PointsBasis,
  RehabInterestAssumption,
};

export interface RehabItem {
  id: string;
  category: string;
  description: string;
  quantity: string;
  unit: string;
  costEntryMode: RehabCostEntryMode;
  materialCost: string;
  laborCost: string;
  otherCost: string;
  notes?: string;
  /** @deprecated migrated → materialCost */
  cost?: string;
}

export interface MarketSnapshot {
  area: string;
  properties: number;
  averagePrice: number;
  rawData?: unknown;
}

/** Non-financial property condition for guidance (does not invent rehab $). */
export type RehabCondition = 'light' | 'moderate' | 'heavy' | 'custom' | '';

export interface DealSetupReviewed {
  property: boolean;
  rehab: boolean;
  financing: boolean;
  operating: boolean;
  exitAssumptions: boolean;
}

export const emptySetupReviewed = (): DealSetupReviewed => ({
  property: false,
  rehab: false,
  financing: false,
  operating: false,
  exitAssumptions: false,
});

export const DEAL_FACTORY_DEFAULTS = {
  closingCostsPercent: '6',
  desiredROI: '12',
  roiVariance: '2',
  assignmentFee: '10000',
  contingencyRate: '10',
  interestRate: '12',
  loanTerm: '1',
  radius: '5',
  financingType: 'hard_money' as FinancingType,
  purchaseClosingPercent: '2',
  loanPointsPercent: '2',
  holdingPeriodMonths: '6',
  otherMonthlyHolding: '0',
  sellingCostsPercent: '2',
  agentCommissionPercent: '6',
  maoPercent: '70',
  rehabBudgetMode: 'rough' as RehabBudgetMode,
  loanCarryMethod: 'interest_only_full_balance' as LoanCarryMethod,
  pointsBasis: 'purchase_loan' as PointsBasis,
  rehabInterestAssumption: 'full_funding_entire_hold' as RehabInterestAssumption,
  financingTypeConfirmed: true,
  rehabFinanced: false,
} as const;

export interface Deal {
  id: string;
  createdAt: string;
  updatedAt: string;
  address: string;
  askingPrice: string;
  arv: string;
  estimatedRehab: string;
  /** @deprecated Legacy ARV exit-cost bucket; preserved for migration. */
  closingCostsPercent: string;
  desiredROI: string;
  roiVariance: string;
  assignmentFee: string;
  rehabItems: RehabItem[];
  rehabBudgetMode: RehabBudgetMode;
  contingencyRate: string;
  interestRate: string;
  loanTerm: string;
  downPayment: string;
  propertyTax: string;
  insurance: string;
  pmi: string;
  latitude: string;
  longitude: string;
  radius: string;
  marketResults: MarketSnapshot | null;

  /** Linked property id (server). */
  propertyId?: string;
  /** Subject property facts (from properties table). */
  beds: string;
  baths: string;
  sqft: string;
  yearBuilt: string;
  propertyType: string;
  city?: string;
  state?: string;

  /** Portfolio workflow (Phase 9.5). Persisted value `passed` displays as "Passed On". */
  workflowStatus?: 'active' | 'watchlist' | 'offer' | 'passed' | 'archived';
  tags?: string[];
  /** List enrichment from API (optional on older payloads). */
  includedCompCount?: number;
  usableCompCount?: number;

  financingType: FinancingType;
  financingTypeConfirmed: boolean;
  loanCarryMethod: LoanCarryMethod;
  purchaseLoanPercent: string;
  purchaseClosingPercent: string;
  loanPointsPercent: string;
  pointsBasis: PointsBasis;
  pointsCustomAmount: string;
  holdingPeriodMonths: string;
  otherMonthlyHolding: string;
  sellingCostsPercent: string;
  agentCommissionPercent: string;
  maoPercent: string;
  closingCostMigration?: ClosingCostMigration;

  rehabFinanced: boolean;
  rehabLoanAmount: string;
  rehabLoanPercent: string;
  rehabInterestAssumption: RehabInterestAssumption;
  lenderFees: string;

  rehabCondition?: RehabCondition;
  setupReviewed?: DealSetupReviewed;
}

export const migrateRehabItem = (raw: Partial<RehabItem> & { id?: string }): RehabItem => {
  const costEntryMode: RehabCostEntryMode =
    raw.costEntryMode === 'per_unit' ? 'per_unit' : 'totals';
  let materialCost = raw.materialCost !== undefined ? String(raw.materialCost) : '';
  let laborCost = raw.laborCost !== undefined ? String(raw.laborCost) : '';
  let otherCost = raw.otherCost !== undefined ? String(raw.otherCost) : '';
  if (!materialCost && !laborCost && !otherCost && raw.cost !== undefined && raw.cost !== '') {
    materialCost = String(raw.cost);
  }
  return {
    id: raw.id || `rehab-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    category: raw.category || '',
    description: raw.description || '',
    quantity: raw.quantity !== undefined ? String(raw.quantity) : '',
    unit: raw.unit || '',
    costEntryMode,
    materialCost,
    laborCost,
    otherCost,
    notes: raw.notes || '',
  };
};

export const REHAB_CATEGORIES = [
  'Kitchen',
  'Bathroom',
  'Flooring',
  'Paint',
  'Roofing',
  'HVAC',
  'Electrical',
  'Plumbing',
  'Windows / Doors',
  'Exterior',
  'Landscaping',
  'Structural',
  'Permits',
  'Cleanup / Dumpster',
  'Miscellaneous',
] as const;

export const createEmptyDeal = (partial: Partial<Deal> = {}): Deal => {
  const { setupReviewed: reviewedPartial, rehabItems: itemsPartial, ...rest } = partial;
  const rehabItems = Array.isArray(itemsPartial)
    ? itemsPartial.map((i) => migrateRehabItem(i))
    : [];
  return {
    id:
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `deal-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    address: '',
    askingPrice: '',
    arv: '',
    estimatedRehab: '',
    closingCostsPercent: DEAL_FACTORY_DEFAULTS.closingCostsPercent,
    desiredROI: DEAL_FACTORY_DEFAULTS.desiredROI,
    roiVariance: DEAL_FACTORY_DEFAULTS.roiVariance,
    assignmentFee: DEAL_FACTORY_DEFAULTS.assignmentFee,
    rehabBudgetMode: DEAL_FACTORY_DEFAULTS.rehabBudgetMode,
    contingencyRate: DEAL_FACTORY_DEFAULTS.contingencyRate,
    interestRate: DEAL_FACTORY_DEFAULTS.interestRate,
    loanTerm: DEAL_FACTORY_DEFAULTS.loanTerm,
    downPayment: '',
    propertyTax: '',
    insurance: '',
    pmi: '',
    latitude: '',
    longitude: '',
    radius: DEAL_FACTORY_DEFAULTS.radius,
    marketResults: null,
    propertyId: undefined,
    beds: '',
    baths: '',
    sqft: '',
    yearBuilt: '',
    propertyType: '',
    city: '',
    state: '',
    workflowStatus: 'active',
    tags: [],
    includedCompCount: 0,
    usableCompCount: 0,
    financingType: DEAL_FACTORY_DEFAULTS.financingType,
    financingTypeConfirmed: DEAL_FACTORY_DEFAULTS.financingTypeConfirmed,
    loanCarryMethod: DEAL_FACTORY_DEFAULTS.loanCarryMethod,
    purchaseLoanPercent: '',
    purchaseClosingPercent: DEAL_FACTORY_DEFAULTS.purchaseClosingPercent,
    loanPointsPercent: DEAL_FACTORY_DEFAULTS.loanPointsPercent,
    pointsBasis: DEAL_FACTORY_DEFAULTS.pointsBasis,
    pointsCustomAmount: '',
    holdingPeriodMonths: DEAL_FACTORY_DEFAULTS.holdingPeriodMonths,
    otherMonthlyHolding: DEAL_FACTORY_DEFAULTS.otherMonthlyHolding,
    sellingCostsPercent: DEAL_FACTORY_DEFAULTS.sellingCostsPercent,
    agentCommissionPercent: DEAL_FACTORY_DEFAULTS.agentCommissionPercent,
    maoPercent: DEAL_FACTORY_DEFAULTS.maoPercent,
    rehabFinanced: DEAL_FACTORY_DEFAULTS.rehabFinanced,
    rehabLoanAmount: '',
    rehabLoanPercent: '',
    rehabInterestAssumption: DEAL_FACTORY_DEFAULTS.rehabInterestAssumption,
    lenderFees: '',
    rehabCondition: '',
    ...rest,
    rehabItems,
    setupReviewed: {
      ...emptySetupReviewed(),
      ...(reviewedPartial || {}),
    },
  };
};

const inferRehabBudgetMode = (raw: Partial<Deal>): RehabBudgetMode => {
  if (raw.rehabBudgetMode === 'rough' || raw.rehabBudgetMode === 'detailed') {
    return raw.rehabBudgetMode;
  }
  return Array.isArray(raw.rehabItems) && raw.rehabItems.length > 0 ? 'detailed' : 'rough';
};

/**
 * Safe migration for deals saved before Phase 2 / Phase 6.
 * Legacy financed is preserved (not remapped to hard_money).
 */
export const migrateDeal = (raw: Partial<Deal> & { id?: string }): Deal => {
  const rehabItems = Array.isArray(raw.rehabItems)
    ? raw.rehabItems.map((i) => migrateRehabItem(i))
    : [];
  const rehabBudgetMode = inferRehabBudgetMode(raw);
  const financingType = (raw.financingType as FinancingType) || 'financed';
  const financingTypeConfirmed =
    raw.financingTypeConfirmed !== undefined
      ? Boolean(raw.financingTypeConfirmed)
      : financingType !== 'financed';

  const phase6Defaults: Partial<Deal> = {
    rehabItems,
    rehabBudgetMode,
    financingType,
    financingTypeConfirmed,
    loanCarryMethod:
      (raw.loanCarryMethod as LoanCarryMethod) || DEAL_FACTORY_DEFAULTS.loanCarryMethod,
    pointsBasis: (raw.pointsBasis as PointsBasis) || DEAL_FACTORY_DEFAULTS.pointsBasis,
    pointsCustomAmount: raw.pointsCustomAmount !== undefined ? String(raw.pointsCustomAmount) : '',
    purchaseLoanPercent:
      raw.purchaseLoanPercent !== undefined ? String(raw.purchaseLoanPercent) : '',
    rehabFinanced: Boolean(raw.rehabFinanced),
    rehabLoanAmount: raw.rehabLoanAmount !== undefined ? String(raw.rehabLoanAmount) : '',
    rehabLoanPercent: raw.rehabLoanPercent !== undefined ? String(raw.rehabLoanPercent) : '',
    rehabInterestAssumption:
      (raw.rehabInterestAssumption as RehabInterestAssumption) ||
      DEAL_FACTORY_DEFAULTS.rehabInterestAssumption,
    lenderFees: raw.lenderFees !== undefined ? String(raw.lenderFees) : '',
  };

  if (raw.closingCostMigration) {
    return createEmptyDeal({ ...raw, ...phase6Defaults, closingCostMigration: raw.closingCostMigration });
  }

  const hasPhase2ExitKeys =
    Object.prototype.hasOwnProperty.call(raw, 'agentCommissionPercent') ||
    Object.prototype.hasOwnProperty.call(raw, 'sellingCostsPercent');

  if (hasPhase2ExitKeys) {
    return createEmptyDeal({ ...raw, ...phase6Defaults });
  }

  const legacyClosing =
    raw.closingCostsPercent !== undefined && raw.closingCostsPercent !== ''
      ? String(raw.closingCostsPercent)
      : '6';

  return createEmptyDeal({
    ...raw,
    ...phase6Defaults,
    agentCommissionPercent: legacyClosing,
    sellingCostsPercent: '0',
    purchaseClosingPercent: '0',
    financingType,
    holdingPeriodMonths:
      raw.holdingPeriodMonths !== undefined ? String(raw.holdingPeriodMonths) : '6',
    loanPointsPercent: raw.loanPointsPercent !== undefined ? String(raw.loanPointsPercent) : '0',
    otherMonthlyHolding:
      raw.otherMonthlyHolding !== undefined ? String(raw.otherMonthlyHolding) : '0',
    maoPercent: raw.maoPercent !== undefined ? String(raw.maoPercent) : '70',
    closingCostMigration: 'legacy_arv_exit_to_commission_v1',
  });
};
